import { useCallback, useMemo, useState } from 'react';
import JSZip from 'jszip';
import { createPdfBlob } from '../lib/pdf';
import type { PdfContentPayload } from '../lib/pdf';
import { slugify } from '../utils/slugify';

export interface PdfGenerationResult {
  title: string;
  filename: string;
  blob: Blob;
}

type GenerateOptions = Omit<PdfContentPayload, 'title'>;

export function usePdfGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTitle, setCurrentTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PdfGenerationResult[]>([]);

  const generatePdfs = useCallback(
    async (titles: string[], options: GenerateOptions) => {
      if (isGenerating) return results;

      const normalizedTitles = titles
        .map((title) => title.trim())
        .filter((title) => title.length > 0);

      if (normalizedTitles.length === 0) {
        const issue = 'Add at least one valid title (one per line).';
        setError(issue);
        throw new Error(issue);
      }

      setIsGenerating(true);
      setError(null);
      setProgress(0);
      setCurrentTitle(null);

      try {
        const generated: PdfGenerationResult[] = [];
        for (let i = 0; i < normalizedTitles.length; i += 1) {
          const title = normalizedTitles[i];
          setCurrentTitle(title);

          const blob = await createPdfBlob({
            ...options,
            title,
          });

          generated.push({
            title,
            filename: `${slugify(title)}.pdf`,
            blob,
          });

          setProgress((i + 1) / normalizedTitles.length);
        }

        setResults(generated);
        setCurrentTitle(null);
        return generated;
      } catch (cause) {
        const message =
          cause instanceof Error ? cause.message : 'PDF generation failed. Please try again.';
        setError(message);
        setCurrentTitle(null);
        setResults([]);
        throw new Error(message, { cause });
      } finally {
        setIsGenerating(false);
      }
    },
    [isGenerating, results],
  );

  const reset = useCallback(() => {
    setIsGenerating(false);
    setProgress(0);
    setCurrentTitle(null);
    setError(null);
    setResults([]);
  }, []);

  const hasResults = results.length > 0;

  const zipAll = useCallback(
    async (archiveName = 'ultimate-pdf-generator.zip') => {
      if (results.length === 0) {
        throw new Error('No PDFs available to bundle.');
      }
      const zip = new JSZip();
      results.forEach((result) => {
        zip.file(result.filename, result.blob);
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      return { blob, filename: archiveName };
    },
    [results],
  );

  return useMemo(
    () => ({
      isGenerating,
      progress,
      currentTitle,
      error,
      results,
      hasResults,
      generatePdfs,
      reset,
      zipAll,
    }),
    [isGenerating, progress, currentTitle, error, results, hasResults, generatePdfs, reset, zipAll],
  );
}
